/**
 * Session-only org / facility context (RBAC Phase 4).
 * Never persist — permissions must not survive reload in localStorage.
 */

import { create } from "zustand";
import type { OrgMembership } from "../types/orgContext";

export interface OrgContextState {
  memberships: OrgMembership[];
  activeOrganizationId: number | null;
  activeProviderId: number | null;
  orgRole: string | null;
  orgRoleName: string | null;
  orgPermissions: string[];
  requiresExplicitContext: boolean;
  loaded: boolean;
  loading: boolean;
  error: string | null;
  setFromResponse: (payload: {
    memberships: OrgMembership[];
    active_organization_id: number | null;
    active_provider_id: number | null;
    org_role: string | null;
    org_role_name: string | null;
    org_permissions: string[];
    requires_explicit_context: boolean;
  }) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clear: () => void;
}

const emptyState = {
  memberships: [] as OrgMembership[],
  activeOrganizationId: null as number | null,
  activeProviderId: null as number | null,
  orgRole: null as string | null,
  orgRoleName: null as string | null,
  orgPermissions: [] as string[],
  requiresExplicitContext: false,
  loaded: false,
  loading: false,
  error: null as string | null,
};

export const useOrgContextStore = create<OrgContextState>()((set) => ({
  ...emptyState,
  setFromResponse: (payload) =>
    set({
      memberships: payload.memberships ?? [],
      activeOrganizationId: payload.active_organization_id ?? null,
      activeProviderId: payload.active_provider_id ?? null,
      orgRole: payload.org_role ?? null,
      orgRoleName: payload.org_role_name ?? null,
      orgPermissions: payload.org_permissions ?? [],
      requiresExplicitContext: Boolean(payload.requires_explicit_context),
      loaded: true,
      loading: false,
      error: null,
    }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error, loading: false }),
  clear: () => set({ ...emptyState }),
}));
